import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { formatSaleAmountPercent, getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import bigDecimal from 'js-big-decimal';
import useTranslation from 'next-translate/useTranslation';
import { twJoin } from 'tailwind-merge';
import { ProductPriceType } from 'types/price';

type B2bProductPriceType = {
    sellingPrice: ProductPriceType;
    basePrice: ProductPriceType;
    variant: 'detail' | 'list' | 'narrow' | 'quick-order';
    isUnitPrice: boolean;
    b2bPaymentDiscount: number | null;
};

export const B2bProductPrice: FC<B2bProductPriceType> = ({
    sellingPrice,
    basePrice,
    isUnitPrice,
    b2bPaymentDiscount,
    variant = 'detail',
}) => {
    const { t } = useTranslation();

    const domainConfig = useDomainConfig();
    const { formatPriceNumber: formatPrice } = useFormatPrice();

    const finalPrice = getSellingPriceByDomain(sellingPrice, domainConfig);
    const baseFinalPrice = getSellingPriceByDomain(basePrice, domainConfig);

    const isSpecialPrice = finalPrice < baseFinalPrice;
    const saleAmountPercent = isSpecialPrice
        ? bigDecimal.multiply(bigDecimal.divide(bigDecimal.subtract(baseFinalPrice, finalPrice), baseFinalPrice), 100)
        : b2bPaymentDiscount ?? 0;
    const saleAmountPercentNumber = new bigDecimal(saleAmountPercent);
    const displaySaleAmount = saleAmountPercentNumber.compareTo(new bigDecimal(0)) === 1;

    if (variant === 'detail') {
        return (
            <div className="flex flex-col gap-y-1">
                {displaySaleAmount && (
                    <div className="font-bold">
                        {t('Discount')}&nbsp;
                        <span className="text-red">{formatSaleAmountPercent(saleAmountPercentNumber)}&nbsp;%</span>
                    </div>
                )}
                {isSpecialPrice && (
                    <>
                        <div>
                            {t('Customer price [short]')}:&nbsp;
                            <span className="text-h6 font-bold">{formatPrice(baseFinalPrice)}</span>&nbsp;
                            {t('excluding VAT')}
                            {isUnitPrice && `/${t('pc')}`}
                        </div>
                        {basePrice.b2bRecommendedPrice && (
                            <div className="text-grayDark">
                                {t('Recommended retail price [short]')}:&nbsp;
                                <span className="text-h6 font-bold">{formatPrice(basePrice.b2bRecommendedPrice)}</span>
                                &nbsp;
                                {t('including VAT')}
                                {isUnitPrice && `/${t('pc')}`}
                            </div>
                        )}
                    </>
                )}
                <div className={twJoin(isSpecialPrice && 'text-red')}>
                    {isSpecialPrice ? t('Special customer price [short]') : t('Customer price [short]')}:&nbsp;
                    <span className="text-h6 font-bold">{formatPrice(finalPrice)}</span>&nbsp;{t('excluding VAT')}
                    {isUnitPrice && `/${t('pc')}`}
                </div>
                {sellingPrice.b2bRecommendedPrice &&
                    (!isSpecialPrice ||
                        basePrice.b2bRecommendedPrice === null ||
                        formatPrice(sellingPrice.b2bRecommendedPrice) !==
                            formatPrice(basePrice.b2bRecommendedPrice)) && (
                        <div>
                            {isSpecialPrice
                                ? t('Special recommended retail price [short]')
                                : t('Recommended retail price [short]')}
                            :&nbsp;
                            <span className="text-h6 font-bold">{formatPrice(sellingPrice.b2bRecommendedPrice)}</span>
                            &nbsp;
                            {t('including VAT')}
                            {isUnitPrice && `/${t('pc')}`}
                        </div>
                    )}
            </div>
        );
    }

    if (variant === 'list') {
        return (
            <div className="flex flex-col gap-y-1 text-primary">
                {displaySaleAmount && (
                    <div className="font-bold">
                        {t('Discount')}&nbsp;
                        <span className="text-red">{formatSaleAmountPercent(saleAmountPercentNumber)}&nbsp;%</span>
                    </div>
                )}
                <div className={twJoin(isSpecialPrice && 'text-red')}>
                    {t('Customer price [short]')}:{' '}
                    <span className="whitespace-nowrap text-h6 font-bold">{formatPrice(finalPrice)}</span>{' '}
                    {t('excluding VAT')}
                </div>
            </div>
        );
    }

    if (variant === 'quick-order') {
        return (
            <div className="flex flex-col text-primary">
                {displaySaleAmount && (
                    <div className="font-bold">
                        {t('Discount')}&nbsp;
                        <span className="text-red">{formatSaleAmountPercent(saleAmountPercentNumber)}&nbsp;%</span>
                    </div>
                )}
                <div className={twJoin(isSpecialPrice && 'text-red')}>
                    {t('Customer price [short]')}:&nbsp;
                    <span className="text-h6 font-bold">{formatPrice(finalPrice)}</span>&nbsp;{t('excluding VAT')}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col text-primary">
            {displaySaleAmount && (
                <div className="pb-1 font-bold">
                    {t('Discount')}&nbsp;
                    <span className="text-red">{formatSaleAmountPercent(saleAmountPercentNumber)}&nbsp;%</span>
                </div>
            )}
            <div className={twJoin(isSpecialPrice && 'text-red')}>
                {t('Customer price [short]')}:&nbsp;<span className="text-h6 font-bold">{formatPrice(finalPrice)}</span>
            </div>
            <div className="text-small leading-6 text-primary">{t('excluding VAT')}</div>
        </div>
    );
};
